// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { Action } from "redux"
import {
  IdList,
  SetActivityTypeRequest,
  SetActivityTypeResponse
} from "generated/wisetime/user/timeline/v4/timeline_pb"
import * as Actions from "../actions"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"

type SetActivityTypeRpc = (request: SetActivityTypeRequest) => Observable<SetActivityTypeResponse>

/**
 * A time row or group can only have one activity type attached to it at a time, for a given site.
 * This constraint is enforced implicitly by the backend e.g. when it receives a request to attach a new activity type
 * to an existing row or group it will replace / override the existing one.
 */
export const setActivityType = (setActivityTypeRpc: SetActivityTypeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.setActivityType.match),
      mergeMap(({ payload: { setActivityTypeRequest, activityType }}) =>
        of(setActivityTypeRequest)
          .pipe(
            mergeMap(setActivityTypeRpc),
            mergeMap(_ => {
              const sliceUpdates = []

              if (setActivityTypeRequest.hasTimeRowIds()) {
                sliceUpdates.push(of(TimelineSlice.attachTimeRowsActivityType({
                  timeRowIds: setActivityTypeRequest.getTimeRowIds() ?? new IdList().setIdsList([]),
                  activityType,
                })))
              }
              const [first] = sliceUpdates
              if (sliceUpdates.length === 1 && first !== undefined) {
                return first
              }
              return concat(...sliceUpdates)
            }
            ),
            catchError(handleError),
          )
      ),
    )

const setActivityTypeRpc = (request: SetActivityTypeRequest): Observable<SetActivityTypeResponse> =>
  grpc(() => timelineClient.setActivityType(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_activity_type_failed_not_found", message: "Failed to add activity type. Could not find the target time row." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_add_activity_type_failed_permission_denied", message: "Failed to add activity type. You do not have permission to add a activity type to the time row." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_activity_type_failed_precondition", message: "Failed to set activity type. Time row may be out of sync." }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_add_activity_type_failed", message: "Failed to add activity type. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default setActivityType(setActivityTypeRpc)
